import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import {
  StackLayout,
  HeadingText,
  GDSHeadingLevel,
  GDSIconSource,
  Box,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { SidebarLayout } from '@leagueplatform/genesis-core-alpha';
import { useIntl } from '@leagueplatform/locales';
import { useUniqueId } from '@leagueplatform/web-common';
import {
  addToActionAnalytics,
  MasonryTextAction,
} from '@leagueplatform/masonry-renderers';
import { IdCard as IdCardComponent } from '#components/id-card/id-card.component.tsx';
import { IdCardError } from '#components/id-card/id-card-error.component.tsx';
import { getSubHeadingLevel } from '#utils/get-sub-heading-level.util.ts';
import { PolicyInformationItem } from '#components/policy-information/policy-information.component.tsx';
import { ZoomCardModal } from '#components/zoom-modal/zoom-modal.component.tsx';

export const NODE_TYPE_ID_CARD_WIDGET = 'idCardWidget';

const MAX_CARD_WIDTH = '384px';

interface PolicyInformation {
  label: string;
  value: string;
}

interface CardImage {
  url: string;
  description?: string;
  aspectRatio?: string;
  type: 'url' | 'base64';
  hasBorder?: boolean;
}

interface Icon {
  icon?: GDSIconSource | string;
  description?: string;
}

type CardActionType =
  | 'print'
  | 'download'
  | 'orderNewCard'
  | 'reportCardLostStolen';

interface CardAction {
  leadingIcon?: Icon;
  label: string;
  type: CardActionType;
}

interface CardError {
  title: string;
  description: string;
}

type IdCardWidgetProps = {
  heading: string;
  headingLevel?: GDSHeadingLevel;
  frontCard?: CardImage;
  backCard?: CardImage;
  policyInformation: PolicyInformation[];
  cardActions: CardAction[];
  printSelectorId?: string;
  cardError?: {
    front?: CardError;
    back?: CardError;
  };
  isZoomable?: boolean;
  benefitType?: string;
};

export type IdCardWidgetNodeActions = {
  onLoad?: MasonryEngineNodeAction;
  onPrintClick: MasonryEngineNodeAction;
  onDownloadClick: MasonryEngineNodeAction;
  onOrderNewCardClick: MasonryEngineNodeAction;
  onReportCardLostStolenClick: MasonryEngineNodeAction;
  onZoomClick: MasonryEngineNodeAction;
};

type IdCardWidgetNode = MasonryEngineNode<
  typeof NODE_TYPE_ID_CARD_WIDGET,
  IdCardWidgetProps,
  IdCardWidgetNodeActions,
  {}
>;

export type IdCardWidgetRendererProps =
  MasonryEngineNodeRendererProps<IdCardWidgetNode>;

interface IdCardProps {
  card?: CardImage;
  error?: CardError;
  isZoomable?: boolean;
  onZoomClick: (event: React.SyntheticEvent) => void;
  cardType: 'front' | 'back';
  title: string;
  headingId?: string;
}

function IdCard({
  card,
  error,
  isZoomable,
  onZoomClick,
  cardType,
  title,
  headingId,
}: IdCardProps) {
  if (error) {
    return <IdCardError title={error.title} description={error.description} />;
  }

  if (!card) {
    return null;
  }

  if (isZoomable) {
    return (
      <ZoomCardModal
        cardType={cardType}
        onZoomClick={onZoomClick}
        title={title}
        triggerLabelId={headingId}
      >
        <IdCardComponent
          source={card.url}
          description={card.description}
          aspectRatio={card.aspectRatio}
          type={card.type}
          hasBorder={card.hasBorder}
        />
      </ZoomCardModal>
    );
  }

  return (
    <IdCardComponent
      source={card.url}
      description={card.description}
      aspectRatio={card.aspectRatio}
      type={card.type}
      hasBorder={card.hasBorder}
    />
  );
}

export function IdCardWidgetRenderer({
  heading,
  headingLevel = '2',
  frontCard: frontImage,
  backCard: backImage,
  policyInformation,
  cardActions,
  onDownloadClick,
  onOrderNewCardClick,
  onPrintClick,
  onReportCardLostStolenClick,
  onZoomClick,
  printSelectorId,
  benefitType,
  cardError,
  isZoomable,
}: IdCardWidgetRendererProps) {
  const { formatMessage } = useIntl();
  const cardActionsId = useUniqueId({ prefix: 'card-actions' });
  const headingId = useUniqueId({ prefix: 'id-card' });
  const hasFrontCard = frontImage || cardError?.front;
  const hasBackCard = backImage || cardError?.back;
  const hasCards = hasFrontCard || hasBackCard;
  const hasPolicyInformation = policyInformation?.length > 0;
  const hasCardActions = React.useMemo(
    () =>
      cardActions?.length > 0 &&
      [
        onDownloadClick,
        onOrderNewCardClick,
        onPrintClick,
        onReportCardLostStolenClick,
      ].some((action) => action !== undefined),
    [
      cardActions,
      onDownloadClick,
      onOrderNewCardClick,
      onPrintClick,
      onReportCardLostStolenClick,
    ],
  );
  const invokeAction = useMasonryEngineActionEmitter();
  const subHeadingLevel = getSubHeadingLevel(headingLevel);
  const cardSectionCss = React.useMemo(
    () => ({
      maxWidth: MAX_CARD_WIDTH,
    }),
    [],
  );

  const actionMap = React.useMemo<
    Record<CardActionType, MasonryEngineNodeAction | undefined>
  >(
    () => ({
      print: onPrintClick,
      download: onDownloadClick,
      orderNewCard: onOrderNewCardClick,
      reportCardLostStolen: onReportCardLostStolenClick,
    }),
    [
      onPrintClick,
      onDownloadClick,
      onOrderNewCardClick,
      onReportCardLostStolenClick,
    ],
  );

  const handleCardActionClick = React.useCallback(
    (event: React.SyntheticEvent, actionType: CardActionType) => {
      const action = actionMap[actionType];
      if (action) {
        invokeAction({ event, ...action });
      }
    },
    [invokeAction, actionMap],
  );

  const handleZoomActionClick = React.useCallback(
    (event: React.SyntheticEvent, cardFace: string) => {
      if (onZoomClick) {
        const analyticsId = `${
          benefitType ? `${benefitType}-` : ''
        }id-card-${cardFace}-zoom-card`;
        const content = {
          id: analyticsId,
          name: 'zoom card',
        };

        invokeAction({
          event,
          ...addToActionAnalytics(
            // TODO: Fix this type needing to be casted
            onZoomClick,
            { content },
          ),
        });
      }
    },
    [invokeAction, onZoomClick, benefitType],
  );

  return (
    <StackLayout spacing="$oneAndHalf" as="section" aria-labelledby={headingId}>
      <HeadingText id={headingId} level={headingLevel} size="lg">
        {heading}
      </HeadingText>

      <SidebarLayout>
        {/* Images Section */}
        {hasCards && (
          <StackLayout
            data-testid="id-card-images-section"
            data-print-card={printSelectorId}
            spacing="$oneAndHalf"
            css={cardSectionCss}
          >
            {hasFrontCard && (
              <IdCard
                card={frontImage}
                cardType="front"
                error={cardError?.front}
                isZoomable={isZoomable}
                onZoomClick={(event) => handleZoomActionClick(event, 'front')}
                title={heading}
                headingId={headingId}
              />
            )}

            {hasBackCard && (
              <IdCard
                card={backImage}
                cardType="back"
                error={cardError?.back}
                isZoomable={isZoomable}
                onZoomClick={(event) => handleZoomActionClick(event, 'back')}
                title={heading}
                headingId={headingId}
              />
            )}
          </StackLayout>
        )}

        {/* Policy Information Section */}
        <StackLayout spacing="$oneAndHalf">
          {hasPolicyInformation && (
            <StackLayout as="ul" spacing="$oneAndHalf">
              {policyInformation.map(({ label, value }) => (
                <PolicyInformationItem
                  key={label}
                  label={label}
                  value={value}
                />
              ))}
            </StackLayout>
          )}
          {/* Card Actions Section */}
          {hasCardActions && (
            <StackLayout
              spacing="$one"
              as="section"
              aria-labelledby={cardActionsId}
            >
              <UtilityText
                as={HeadingText}
                css={{
                  typography: '$overline',
                  textTransform: 'uppercase',
                }}
                id={cardActionsId}
                level={subHeadingLevel}
              >
                {formatMessage({
                  id: 'BENEFITS_CARD_ACTIONS_HEADING',
                })}
              </UtilityText>
              <StackLayout as="ul" spacing="$one">
                {cardActions.map(({ leadingIcon, label, type }) => {
                  const action = actionMap[type];
                  if (!action) {
                    return null;
                  }

                  return (
                    <Box as="li" key={label}>
                      <MasonryTextAction
                        key={label}
                        icon={leadingIcon?.icon}
                        onClick={(event) => handleCardActionClick(event, type)}
                        action={action}
                        css={{ textAlign: 'start' }}
                      >
                        {label}
                      </MasonryTextAction>
                    </Box>
                  );
                })}
              </StackLayout>
            </StackLayout>
          )}
        </StackLayout>
      </SidebarLayout>
    </StackLayout>
  );
}
